import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Footprints } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { VoiceNoteRecorder } from "@/components/news/VoiceNoteRecorder";

const API = process.env.REACT_APP_BACKEND_URL;

// A walk someone passed on: press play, no login, no sign-up.
export default function SharedWalk() {
  const { token } = useParams();
  const [walk, setWalk] = useState(null);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState([]);
  const [name, setName] = useState(() => localStorage.getItem("chatmilu.walk.name") || "");

  useEffect(() => {
    api
      .get(`/news/walks/shared/${token}`)
      .then(({ data }) => setWalk(data))
      .catch((e) => setError(apiError(e.response?.data?.detail) || "That walk link is no longer live"));
    api.get(`/news/walks/shared/${token}/notes`)
      .then(({ data }) => setNotes(data.notes))
      .catch(() => {});
  }, [token]);

  const sendNote = async (blob) => {
    try {
      const fd = new FormData();
      fd.append("audio", blob, "note.webm");
      fd.append("name", name);
      const { data } = await api.post(`/news/walks/shared/${token}/notes`, fd,
                                     { headers: { "Content-Type": "multipart/form-data" } });
      setNotes((rows) => [...rows, data]);
      localStorage.setItem("chatmilu.walk.name", name);
      toast.success("Note sent — they'll hear it next time they open the walk");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send that note");
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-16 sm:px-8" data-testid="shared-walk-page">
      <p className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-brand">
        <Footprints className="h-3.5 w-3.5" /> Shared walk
      </p>
      <h1 className="mt-3 font-display text-[1.7rem] font-extrabold tracking-tighter sm:text-[2.25rem]">
        {error ? "Nothing here" : walk ? `${walk.owner || "A reader"}'s walk` : "Opening…"}
      </h1>

      {error && <p className="mt-3 text-sm text-ink-soft" data-testid="shared-walk-error">{error}</p>}

      {walk && (
        <>
          <p className="mt-2 text-sm text-ink-soft">
            {walk.stories} stories · about {walk.minutes} minutes of reading, read aloud in one run
          </p>
          <audio
            controls
            preload="none"
            data-testid="shared-walk-audio"
            src={`${API}/api/news/walks/shared/${token}/audio`}
            className="mt-5 w-full"
          />
          <ol className="mt-6 space-y-3">
            {walk.titles?.map((story, i) => (
              <li key={story.story_id} className="rounded-2xl border border-white/10 bg-panel p-4"
                  data-testid={`shared-walk-story-${story.story_id}`}>
                <a href={story.url} target="_blank" rel="noreferrer" dir="auto"
                   className="text-base font-semibold hover:text-brand">
                  {i + 1}. {story.title}
                </a>
                <p className="mt-1 text-xs text-ink-soft">
                  {story.source}
                  {story.read_min ? <span className="text-brand">{` · ${story.read_min} min read`}</span> : null}
                </p>
              </li>
            ))}
          </ol>
        </>
      )}

      {walk && (
        <div className="mt-8 rounded-2xl border border-white/10 bg-panel p-4" data-testid="walk-notes">
          <p className="text-[11px] uppercase tracking-widest text-brand">Say something back</p>
          {notes.length > 0 && (
            <ul className="mt-3 space-y-2">
              {notes.map((note) => (
                <li key={note.note_id}
                    className={`rounded-xl px-3 py-2 text-sm ${
                      note.mine ? "border border-brand/40 bg-brand/10" : "bg-white/5"}`}
                    data-testid={`walk-note-${note.note_id}`} dir="auto">
                  <span className="font-semibold text-brand">
                    {note.name}{note.mine ? " (the walker)" : ""}
                  </span>{" "}
                  <span className="text-ink-soft">{note.text}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              data-testid="walk-note-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              placeholder="Your name"
              className="min-h-[36px] w-40 rounded-full border border-white/10 bg-page px-3 text-sm outline-none focus:border-brand/50"
            />
            <VoiceNoteRecorder onSend={sendNote} testid="walk-note-record" />
            <span className="text-xs text-ink-soft">MiLu writes it down for them to read or hear.</span>
          </div>
        </div>
      )}

      <p className="mt-10 text-xs text-ink-soft">
        Headlines come from publishers — every link opens the original article.
      </p>
    </div>
  );
}
